<script lang="ts" setup>
import { onMounted, onUnmounted, ref } from 'vue'

import { getLanguage } from '@/helpers/language-helper'
import { useModalPopup } from '@/services/seoGlobalService'
import {
  BRAZIL_REGULATIONS_POPUP_OPEN_EVENT,
  notifyBrazilRegulationsPopupAccepted
} from '@/utils/brazilRegulationsPopup'

const modalData = useModalPopup()

const isVisible = ref<boolean>(false)
const sessionKey = 'brazilPopupShown'

const supportedLanguages = ['en', 'pt', 'es', 'th', 'ko', 'zh-hans', 'zh-hant', 'jp'] as const
type PopupLanguage = (typeof supportedLanguages)[number]

const defaultTranslations = {
  'Attention!': 'Attention!',
  'Hantec Markets services are not available in Brazil.':
    'Hantec Markets services are not available in Brazil.',
  'If you are a Brazilian national, residing in Brazil and you would like to open a trading account, please visit the official website of Hantec Assessoria de Investimentos LTDA, our authorized entity in Brazil.':
    'If you are a Brazilian national, residing in Brazil and you would like to open a trading account, please visit the official website of Hantec Assessoria de Investimentos LTDA, our authorized entity in Brazil.',
  'STAY ON THE HANTEC GLOBAL WEBSITE': 'STAY ON THE HANTEC GLOBAL WEBSITE',
  'ACCESS HANTEC INVESTMENT ADVISORY LTDA': 'ACCESS HANTEC INVESTMENT ADVISORY LTDA'
} as const

type PopupTranslationKey = keyof typeof defaultTranslations
type PopupTranslations = Record<PopupTranslationKey, string>

const localTranslations: Record<PopupLanguage, PopupTranslations> = {
  en: { ...defaultTranslations },
  pt: {
    ...defaultTranslations,
    'Attention!': 'Atenção!',
    'Hantec Markets services are not available in Brazil.':
      'Os serviços da Hantec Markets não estão disponíveis no Brasil.',
    'If you are a Brazilian national, residing in Brazil and you would like to open a trading account, please visit the official website of Hantec Assessoria de Investimentos LTDA, our authorized entity in Brazil.':
      'Se você estiver acessando a partir do território brasileiro, visite o site oficial da Hantec Assessoria de Investimentos LTDA, nossa entidade autorizada no Brasil.',
    'STAY ON THE HANTEC GLOBAL WEBSITE': 'CONTINUAR NO SITE GLOBAL DA HANTEC',
    'ACCESS HANTEC INVESTMENT ADVISORY LTDA': 'ACESSAR HANTEC INVESTMENT ADVISORY LTDA'
  },
  es: {
    ...defaultTranslations,
    'Attention!': '¡Atención!',
    'Hantec Markets services are not available in Brazil.':
      'Los servicios de Hantec Markets no están disponibles en Brasil.',
    'If you are a Brazilian national, residing in Brazil and you would like to open a trading account, please visit the official website of Hantec Assessoria de Investimentos LTDA, our authorized entity in Brazil.':
      'Si está accediendo desde territorio brasileño, visite el sitio web oficial de Hantec Assessoria de Investimentos LTDA, nuestra entidad autorizada en Brasil:',
    'STAY ON THE HANTEC GLOBAL WEBSITE': 'PERMANECER EN EL SITIO GLOBAL DE HANTEC',
    'ACCESS HANTEC INVESTMENT ADVISORY LTDA': 'ACCEDER A HANTEC INVESTMENT ADVISORY LTDA'
  },
  th: {
    ...defaultTranslations,
    'Attention!': 'โปรดทราบ!',
    'Hantec Markets services are not available in Brazil.':
      'บริการของ Hantec Markets ไม่สามารถใช้ได้ในประเทศบราซิล',
    'If you are a Brazilian national, residing in Brazil and you would like to open a trading account, please visit the official website of Hantec Assessoria de Investimentos LTDA, our authorized entity in Brazil.':
      'หากคุณกำลังเข้าถึงจากประเทศบราซิล กรุณาเยี่ยมชมเว็บไซต์ทางการของ Hantec Assessoria de Investimentos LTDA ซึ่งเป็นหน่วยงานที่ได้รับอนุญาตของเราในบราซิล:',
    'STAY ON THE HANTEC GLOBAL WEBSITE': 'อยู่บนเว็บไซต์ HANTEC ทั่วโลกต่อไป',
    'ACCESS HANTEC INVESTMENT ADVISORY LTDA': 'เข้าสู่ HANTEC INVESTMENT ADVISORY LTDA'
  },
  ko: {
    ...defaultTranslations,
    'Attention!': '주의!',
    'Hantec Markets services are not available in Brazil.':
      'Hantec Markets의 서비스는 브라질에서 이용하실 수 없습니다.',
    'If you are a Brazilian national, residing in Brazil and you would like to open a trading account, please visit the official website of Hantec Assessoria de Investimentos LTDA, our authorized entity in Brazil.':
      '브라질에서 접속하시는 경우, 브라질 내 당사의 공인 법인인 Hantec Assessoria de Investimentos LTDA의 공식 웹사이트를 방문해 주시기 바랍니다:',
    'STAY ON THE HANTEC GLOBAL WEBSITE': 'HANTEC 글로벌 웹사이트에 계속 머무르기',
    'ACCESS HANTEC INVESTMENT ADVISORY LTDA': 'HANTEC INVESTMENT ADVISORY LTDA 접속하기'
  },
  'zh-hans': {
    ...defaultTranslations,
    'Attention!': '注意！',
    'Hantec Markets services are not available in Brazil.': 'Hantec Markets 的服务在巴西不可用。',
    'If you are a Brazilian national, residing in Brazil and you would like to open a trading account, please visit the official website of Hantec Assessoria de Investimentos LTDA, our authorized entity in Brazil.':
      '如果您从巴西境内访问，请访问我们在巴西的授权实体 Hantec Assessoria de Investimentos LTDA 的官方网站：',
    'STAY ON THE HANTEC GLOBAL WEBSITE': '继续访问 HANTEC 全球网站',
    'ACCESS HANTEC INVESTMENT ADVISORY LTDA': '访问 HANTEC INVESTMENT ADVISORY LTDA'
  },
  'zh-hant': {
    ...defaultTranslations,
    'Attention!': '注意！',
    'Hantec Markets services are not available in Brazil.': 'Hantec Markets 的服務在巴西不可用。',
    'If you are a Brazilian national, residing in Brazil and you would like to open a trading account, please visit the official website of Hantec Assessoria de Investimentos LTDA, our authorized entity in Brazil.':
      '如果您從巴西境內訪問，請前往我們在巴西的授權實體 Hantec Assessoria de Investimentos LTDA 的官方網站：',
    'STAY ON THE HANTEC GLOBAL WEBSITE': '繼續訪問 HANTEC 全球網站',
    'ACCESS HANTEC INVESTMENT ADVISORY LTDA': '訪問 HANTEC INVESTMENT ADVISORY LTDA'
  },
  jp: {
    ...defaultTranslations,
    'Attention!': 'ご注意ください！',
    'Hantec Markets services are not available in Brazil.':
      'Hantec Marketsのサービスはブラジルではご利用いただけません。',
    'If you are a Brazilian national, residing in Brazil and you would like to open a trading account, please visit the official website of Hantec Assessoria de Investimentos LTDA, our authorized entity in Brazil.':
      'ブラジル国内からアクセスしている場合は、ブラジルにおける当社の認可法人である Hantec Assessoria de Investimentos LTDA の公式ウェブサイトをご覧ください。',
    'STAY ON THE HANTEC GLOBAL WEBSITE': 'HANTECグローバルウェブサイトに留まる',
    'ACCESS HANTEC INVESTMENT ADVISORY LTDA': 'HANTEC INVESTMENT ADVISORY LTDA にアクセス'
  }
}

const TITLE_KEY: PopupTranslationKey = 'Attention!'
const SUBTITLE_KEY: PopupTranslationKey = 'Hantec Markets services are not available in Brazil.'
const DESCRIPTION_KEY: PopupTranslationKey =
  'If you are a Brazilian national, residing in Brazil and you would like to open a trading account, please visit the official website of Hantec Assessoria de Investimentos LTDA, our authorized entity in Brazil.'
const STAY_BUTTON_KEY: PopupTranslationKey = 'STAY ON THE HANTEC GLOBAL WEBSITE'
const REDIRECT_BUTTON_KEY: PopupTranslationKey = 'ACCESS HANTEC INVESTMENT ADVISORY LTDA'

const currentLanguage = ref<PopupLanguage>('en')

const getLanguageFromPath = (): PopupLanguage | null => {
  const pathLangMatch = window.location.pathname.match(
    /^\/(en|pt|es|th|ko|zh-hans|zh-hant|jp)(\/|$)/
  )
  return pathLangMatch ? (pathLangMatch[1] as PopupLanguage) : null
}

const resolvePopupLanguage = (): PopupLanguage => {
  const langFromStorage = getLanguage()
  if (langFromStorage && supportedLanguages.includes(langFromStorage as PopupLanguage)) {
    return langFromStorage as PopupLanguage
  }

  return getLanguageFromPath() || 'en'
}

const setPopupLanguage = () => {
  currentLanguage.value = resolvePopupLanguage()
}

const t = (key: PopupTranslationKey) => {
  const activeTranslations = localTranslations[currentLanguage.value] || localTranslations.en
  return activeTranslations[key] || localTranslations.en[key]
}

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null
  return null
}

const acceptDisclaimer = () => {
  sessionStorage.setItem(sessionKey, 'true')
  notifyBrazilRegulationsPopupAccepted()
  document.body.style.overflow = ''
  isVisible.value = false
}

const leavePage = () => {
  window.location.href = 'https://hmarkets.com.br/'
}

const showPopup = () => {
  if (isVisible.value) {
    return
  }

  setPopupLanguage()
  isVisible.value = true
  document.body.style.overflow = 'hidden'
}

const handleOpenPopup = () => {
  showPopup()
}

onMounted(() => {
  setPopupLanguage()

  const country = getCookie('country')
  const popupShown = sessionStorage.getItem(sessionKey)
  if (country === 'BR' && !popupShown) {
    showPopup()
  }

  window.addEventListener(BRAZIL_REGULATIONS_POPUP_OPEN_EVENT, handleOpenPopup)
})

onUnmounted(() => {
  window.removeEventListener(BRAZIL_REGULATIONS_POPUP_OPEN_EVENT, handleOpenPopup)
  document.body.style.overflow = ''
})
</script>

<template>
  <div
    @click.prevent
    v-if="isVisible"
    class="fixed inset-0 z-[1000] w-full h-full bg-black bg-opacity-85"
  ></div>

  <div
    v-if="isVisible"
    class="fixed inset-0 top-[100px] px-[10px] pointer-events-none z-[3000] flex items-center justify-center w-full h-[85%] overflow-hidden"
  >
    <div
      class="bg-white lg:pb-20 rounded-[10px] shadow-lg w-full max-w-[1200px] lg:h-auto h-full flex flex-col items-center pb-20 relative overflow-auto popup-vector pointer-events-auto"
    >
      <h2 class="mb-4 lg:mb-10 lg:px-0 px-[10px] text-center pt-4 lg:pt-20 h2-bold">
        {{ t(TITLE_KEY) }}
      </h2>
      <h3 class="mb-10 lg:px-0 px-[10px] text-center h3-bold z-10">
        {{ t(SUBTITLE_KEY) }}
      </h3>
      <div
        class="mb-16 lg:mb-20 lg:px-0 px-[10px] t butter-wysiwyg max-w-[960px] w-full z-20 relative text-center lg:text-left"
      >
        {{ t(DESCRIPTION_KEY) }}
      </div>
      <div
        class="flex flex-col justify-center w-full lg:gap-5 gap-[10px] lg:flex-row px-[6px] relative z-30"
      >
        <button
          class="mx-auto lg:mx-0 px-2 py-2 lg:py-4 lg:mr-2 border-2 text-red-150 button rounded w-full max-w-[470px]"
          @click="acceptDisclaimer"
        >
          {{ t(STAY_BUTTON_KEY) }}
        </button>

        <button
          class="mx-auto lg:mx-0 px-[10px] py-2 text-white bg-red-150 rounded w-full max-w-[470px] button"
          @click="leavePage"
        >
          {{ t(REDIRECT_BUTTON_KEY) }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
@media (min-width: 1200px) {
  .popup-vector::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-repeat: no-repeat;
    background-image: url('/assets/popup-vector.png');
    z-index: 10;
    opacity: 1;
  }
}
</style>
